import React, { useEffect, useState } from 'react';

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from './ui';
import { useImportHubReadOnly } from './lib/access';

export interface ImportSavedViewSummaryItem {
  label: string;
  values: string[];
}

export const SaveImportViewDialog: React.FC<{
  open: boolean;
  mode: 'create' | 'rename';
  initialName?: string;
  summary: ImportSavedViewSummaryItem[];
  visibleColumnCount: number;
  saving: boolean;
  error?: string | null;
  onClose: () => void;
  onSave: (name: string) => void;
}> = ({
  open,
  mode,
  initialName = '',
  summary,
  visibleColumnCount,
  saving,
  error,
  onClose,
  onSave,
}) => {
  const readOnly = useImportHubReadOnly();
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const close = () => {
    if (!saving) onClose();
  };
  const submit = () => {
    const trimmed = name.trim();
    if (trimmed && !saving && !readOnly) onSave(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        size='md'
        onClose={close}
        className='ih-saved-view-dialog'
      >
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Save current view' : 'Rename saved view'}
          </DialogTitle>
          <p className='ih-saved-view-dialog-description'>
            {mode === 'create'
              ? 'Save these filters and table options for everyone with access to this database.'
              : 'Change the shared name without altering its saved filters.'}
          </p>
        </DialogHeader>
        <DialogBody>
          <label className='ih-saved-view-field'>
            <span>
              View name <strong aria-hidden='true'>*</strong>
            </span>
            <Input
              value={name}
              autoFocus
              maxLength={120}
              placeholder='e.g. China Section 301 exposure'
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submit();
                }
              }}
            />
          </label>

          {mode === 'create' && (
            <section className='ih-saved-view-summary'>
              <div className='ih-saved-view-summary-heading'>
                Captured in this view
              </div>
              <div className='ih-saved-view-summary-list'>
                {summary.map((item) => (
                  <div className='ih-saved-view-summary-row' key={item.label}>
                    <span>{item.label}</span>
                    <div>
                      {item.values.map((value) => (
                        <span
                          className='ih-saved-view-summary-value'
                          key={`${item.label}-${value}`}
                        >
                          {value}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                <div className='ih-saved-view-summary-row'>
                  <span>Visible columns</span>
                  <div>
                    <span className='ih-saved-view-summary-value'>
                      {visibleColumnCount}
                    </span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {error && (
            <div className='ih-saved-view-dialog-error' role='alert'>
              {error}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant='outline' onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='primary'
            locked={readOnly}
            onClick={submit}
            disabled={saving || !name.trim()}
          >
            {saving
              ? 'Saving…'
              : mode === 'create'
              ? 'Save view'
              : 'Rename view'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
